/**
 * Member Group Service
 * 
 * Service สำหรับจัดการกลุ่มสมาชิก (VIP Tier System)
 * 
 * 🎯 Scale: รองรับสมาชิก 100,000+ คน
 * - ใช้ Cloud Functions สำหรับ bulk operations
 * - REST API สำหรับ single record operations
 */

import { queryClass, createObject, updateObject, getObjectById, callCloudFunction } from '@/services/adapters/parse/parse-client'
import type {
  MemberGroup,
  MemberGroupFormData,
  MemberGroupQueryParams,
  MemberGroupStats,
} from './member-group.types'

export class MemberGroupService {
  /**
   * Map raw Parse data to MemberGroup interface
   */
  private static mapToMemberGroup(raw: any): MemberGroup {
    return {
      id: raw.objectId,
      objectId: raw.objectId,
      name: raw.name || '',
      code: raw.code || '',
      level: raw.level || 1,
      color: raw.color || '#CD7F32',
      icon: raw.icon || 'medal',
      description: raw.description || '',
      is_default: raw.is_default === true,
      is_manual: raw.is_manual === true,
      condition_type: raw.condition_type || 'manual',
      condition_amount: raw.condition_amount || 0,
      lossback_settings: raw.lossback_settings || {},
      deposit_min: raw.deposit_min || 0,
      deposit_max: raw.deposit_max || 0,
      withdraw_max_daily: raw.withdraw_max_daily || 0,
      benefits: raw.benefits || [],
      member_count: raw.member_count || 0,
      status: raw.status || 'Active',
      createdAt: raw.createdAt || new Date().toISOString(),
      updatedAt: raw.updatedAt || new Date().toISOString(),
    }
  }

  /**
   * Find all member groups
   */
  static async find(
    sessionToken: string,
    params: MemberGroupQueryParams = {}
  ): Promise<{ items: MemberGroup[]; total: number }> {
    const where: Record<string, any> = {
      deletedAt: { $exists: false },
    }

    if (params.status) {
      where.status = params.status
    }

    if (params.query) {
      where.$or = [
        { name: { $regex: params.query, $options: 'i' } },
        { code: { $regex: params.query, $options: 'i' } },
      ]
    }

    const queryOptions: any = {
      where,
      limit: params.limit || 100,
      skip: params.page ? (params.page - 1) * (params.limit || 100) : 0,
      order: 'level', // เรียงตามลำดับขั้น
    }

    const result = await queryClass('MemberGroup', queryOptions, sessionToken)

    return {
      items: (result.results || []).map(this.mapToMemberGroup),
      total: result.count || 0,
    }
  }

  /**
   * Get member group by ID
   */
  static async getById(_sessionToken: string, id: string): Promise<MemberGroup | null> {
    try {
      const result = await getObjectById('MemberGroup', id)
      return this.mapToMemberGroup(result)
    } catch (error) {
      console.error('Error getting MemberGroup:', error)
      return null
    }
  }

  /**
   * Get stats
   */
  static async getStats(sessionToken: string): Promise<MemberGroupStats> {
    const [all, active, inactive] = await Promise.all([
      queryClass('MemberGroup', { where: { deletedAt: { $exists: false } }, limit: 0 }, sessionToken),
      queryClass('MemberGroup', { where: { deletedAt: { $exists: false }, status: 'Active' }, limit: 0 }, sessionToken),
      queryClass('MemberGroup', { where: { deletedAt: { $exists: false }, status: 'Inactive' }, limit: 0 }, sessionToken),
    ])

    // นับจำนวนสมาชิกทั้งหมดที่มี member_group
    const membersWithGroup = await queryClass(
      '_User',
      { where: { member_group: { $exists: true } }, limit: 0 },
      sessionToken
    )

    return {
      total: all.count || 0,
      active: active.count || 0,
      inactive: inactive.count || 0,
      totalMembers: membersWithGroup.count || 0,
    }
  }

  /**
   * Check if code exists
   */
  static async checkCodeExists(sessionToken: string, code: string, excludeId?: string): Promise<boolean> {
    const where: Record<string, any> = {
      code: code.toLowerCase(),
      deletedAt: { $exists: false },
    }

    if (excludeId) {
      where.objectId = { $ne: excludeId }
    }

    const result = await queryClass('MemberGroup', { where, limit: 1 }, sessionToken)
    return (result.count || 0) > 0
  }

  /**
   * Create new member group
   */
  static async create(sessionToken: string, data: MemberGroupFormData): Promise<MemberGroup> {
    // ถ้าเป็น default ให้ยกเลิก default ของกลุ่มอื่นก่อน
    if (data.is_default) {
      await this.clearDefaultGroup(sessionToken)
    }

    const saveData = {
      name: data.name,
      code: data.code.toLowerCase(),
      level: data.level,
      color: data.color,
      icon: data.icon,
      description: data.description || '',
      is_default: data.is_default,
      is_manual: data.is_manual,
      condition_type: data.condition_type,
      condition_amount: data.condition_amount,
      lossback_settings: data.lossback_settings,
      deposit_min: data.deposit_min,
      deposit_max: data.deposit_max,
      withdraw_max_daily: data.withdraw_max_daily,
      benefits: data.benefits,
      member_count: 0,
      status: data.status,
    }

    const result = await createObject('MemberGroup', saveData)
    return this.mapToMemberGroup({ ...saveData, objectId: result.objectId, createdAt: result.createdAt })
  }

  /**
   * Update member group
   */
  static async update(sessionToken: string, id: string, data: Partial<MemberGroupFormData>): Promise<MemberGroup> {
    // ถ้าเป็น default ให้ยกเลิก default ของกลุ่มอื่นก่อน
    if (data.is_default) {
      await this.clearDefaultGroup(sessionToken, id)
    }

    const updateData: Record<string, any> = {}

    if (data.name !== undefined) updateData.name = data.name
    if (data.code !== undefined) updateData.code = data.code.toLowerCase()
    if (data.level !== undefined) updateData.level = data.level
    if (data.color !== undefined) updateData.color = data.color
    if (data.icon !== undefined) updateData.icon = data.icon
    if (data.description !== undefined) updateData.description = data.description
    if (data.is_default !== undefined) updateData.is_default = data.is_default
    if (data.is_manual !== undefined) updateData.is_manual = data.is_manual
    if (data.condition_type !== undefined) updateData.condition_type = data.condition_type
    if (data.condition_amount !== undefined) updateData.condition_amount = data.condition_amount
    if (data.lossback_settings !== undefined) updateData.lossback_settings = data.lossback_settings
    if (data.deposit_min !== undefined) updateData.deposit_min = data.deposit_min
    if (data.deposit_max !== undefined) updateData.deposit_max = data.deposit_max
    if (data.withdraw_max_daily !== undefined) updateData.withdraw_max_daily = data.withdraw_max_daily
    if (data.benefits !== undefined) updateData.benefits = data.benefits
    if (data.status !== undefined) updateData.status = data.status

    await updateObject('MemberGroup', id, updateData)
    
    const updated = await this.getById(sessionToken, id)
    return updated!
  }

  /**
   * Delete member group (soft delete)
   */
  static async delete(_sessionToken: string, id: string): Promise<void> {
    await updateObject('MemberGroup', id, {
      deletedAt: { __type: 'Date', iso: new Date().toISOString() },
    })
  }

  /**
   * Clear default flag from all groups
   */
  private static async clearDefaultGroup(sessionToken: string, excludeId?: string): Promise<void> {
    const where: Record<string, any> = {
      is_default: true,
      deletedAt: { $exists: false },
    }

    if (excludeId) {
      where.objectId = { $ne: excludeId }
    }

    const result = await queryClass('MemberGroup', { where, limit: 100 }, sessionToken)

    for (const group of result.results || []) {
      await updateObject('MemberGroup', group.objectId, { is_default: false })
    }
  }

  /**
   * Update member count for a group
   */
  static async updateMemberCount(sessionToken: string, groupId: string): Promise<number> {
    const result = await queryClass(
      '_User',
      {
        where: {
          member_group: { __type: 'Pointer', className: 'MemberGroup', objectId: groupId },
        },
        limit: 0,
      },
      sessionToken
    )

    const count = result.count || 0
    await updateObject('MemberGroup', groupId, { member_count: count })
    return count
  }

  /**
   * Get default group
   */
  static async getDefaultGroup(sessionToken: string): Promise<MemberGroup | null> {
    const result = await queryClass(
      'MemberGroup',
      {
        where: { is_default: true, status: 'Active', deletedAt: { $exists: false } },
        limit: 1,
      },
      sessionToken
    )

    if (result.results && result.results.length > 0) {
      return this.mapToMemberGroup(result.results[0])
    }

    return null
  }

  /**
   * Assign member to group
   */
  static async assignMemberToGroup(sessionToken: string, memberId: string, groupId: string): Promise<void> {
    const group = await this.getById(sessionToken, groupId)
    if (!group) throw new Error('ไม่พบกลุ่มสมาชิก')

    await updateObject('_User', memberId, {
      member_group: { __type: 'Pointer', className: 'MemberGroup', objectId: groupId },
      member_group_code: group.code,
    })

    // อัพเดทจำนวนสมาชิก
    await this.updateMemberCount(sessionToken, groupId)
  }

  // ============================================
  // Cloud Function Wrappers
  // (สำหรับ bulk operations - รองรับ 100,000+ records)
  // ============================================

  /**
   * สร้างกลุ่มเริ่มต้น 4 กลุ่ม (Bronze, Silver, Gold, VIP)
   * 🎯 ใช้ Cloud Function
   */
  static async createDefaultGroups(sessionToken: string): Promise<{ created: string[]; skipped: string[] }> {
    console.log('📤 Calling Cloud Function: createDefaultGroups')
    const result = await callCloudFunction('createDefaultGroups', {}, sessionToken)
    console.log('✅ createDefaultGroups result:', result)
    return {
      created: result.created || [],
      skipped: result.skipped || [],
    }
  }

  /**
   * Assign กลุ่ม default ให้สมาชิกที่ยังไม่มีกลุ่ม
   * 🎯 ใช้ Cloud Function - รองรับหลักแสน (Batch processing ฝั่ง server)
   */
  static async assignDefaultToMembersWithoutGroup(
    sessionToken: string,
    _onProgress?: (current: number, total: number) => void // unused - Cloud Function จัดการเอง
  ): Promise<{ updated: number; failed: number; total: number }> {
    console.log('📤 Calling Cloud Function: assignDefaultGroupToMembers')
    const result = await callCloudFunction('assignDefaultGroupToMembers', {}, sessionToken)
    console.log('✅ assignDefaultGroupToMembers result:', result)
    return {
      updated: result.updated || 0,
      failed: result.failed || 0,
      total: result.total || 0,
    }
  }

  /**
   * นับสมาชิกที่ไม่มีกลุ่ม
   * 🎯 ใช้ Cloud Function
   */
  static async countMembersWithoutGroup(sessionToken: string): Promise<number> {
    console.log('📤 Calling Cloud Function: countMembersWithoutGroup')
    const result = await callCloudFunction('countMembersWithoutGroup', {}, sessionToken)
    return result.count || 0
  }

  /**
   * อัพเดทจำนวนสมาชิกในทุกกลุ่ม
   * 🎯 ใช้ Cloud Function
   */
  static async updateAllGroupCounts(sessionToken: string): Promise<void> {
    console.log('📤 Calling Cloud Function: updateAllGroupMemberCounts')
    await callCloudFunction('updateAllGroupMemberCounts', {}, sessionToken)
  }
}

